/** Shop listing: category filter over a grid of instrument cards. */

import { useSearchParams } from 'react-router-dom'
import { BlurbCard, Eyebrow, Row, Section, StatusBadge } from '../components/ui'
import { formatPrice, type Category, type Product } from '../lib/api'
import { useApi, useDocumentMeta, useHreflang, useLocale } from '../lib/hooks'
import { pagePaths, productPath, t } from '../lib/i18n'

export default function Shop() {
  const locale = useLocale()
  const strings = t(locale)
  const [params, setParams] = useSearchParams()
  const active = params.get('kategorie') ?? params.get('category') ?? ''

  const { data: categories } = useApi<Category[]>(`/api/categories?locale=${locale}`)
  // One fetch for the whole catalogue; the category chips filter client-side,
  // so switching is instant - no refetch, no layout flash.
  const { data: allProducts, loading } = useApi<Product[]>(`/api/products?locale=${locale}`)

  useDocumentMeta(`${strings.shop.title} | zvidori`, strings.meta.shop.description)
  useHreflang(pagePaths('shop'))

  const statusLabel = (status: Product['status']) => strings.shop[status]

  const setCategory = (slug: string) => {
    const next = new URLSearchParams(params)
    // German keeps its historical query-param name; all other locales share
    // the English one, matching the shared non-German URL slugs.
    const key = locale === 'de' ? 'kategorie' : 'category'
    next.delete('kategorie')
    next.delete('category')
    if (slug) next.set(key, slug)
    setParams(next, { replace: true })
  }

  // Two-tier filter: the main row shows top-level categories; picking one that
  // has subcategories reveals a second row of sub-chips beneath it.
  const topLevel = (categories ?? []).filter((category) => !category.parent_slug)
  const activeCategory = (categories ?? []).find((category) => category.slug === active)
  const activeTopSlug = activeCategory?.parent_slug ?? activeCategory?.slug ?? ''
  const subcategories = (categories ?? []).filter(
    (category) => category.parent_slug === activeTopSlug && activeTopSlug,
  )

  // A main category includes its subcategories' instruments.
  const activeSlugs =
    active === ''
      ? null
      : new Set([
          active,
          ...(categories ?? [])
            .filter((category) => category.parent_slug === active)
            .map((category) => category.slug),
        ])
  const products = (allProducts ?? []).filter(
    (product) =>
      activeSlugs === null ||
      (product.category_slug !== null && activeSlugs.has(product.category_slug)),
  )

  const chipClass = (pressed: boolean) =>
    `rounded-full border px-4 py-1.5 font-sans text-[14px] font-semibold transition-colors ${
      pressed ? 'border-ink bg-ink text-paper' : 'border-line bg-transparent text-ink hover:bg-paper-deep'
    }`

  return (
    <>
      <Section className="!pb-6">
        <Row>
          <Eyebrow>{strings.shop.eyebrow}</Eyebrow>
          <h1 className="m-0 mt-3 font-serif text-[clamp(40px,6vw,64px)] font-medium leading-[1.05] text-ink">
            {strings.shop.title}
          </h1>
          <p className="mb-0 mt-5 max-w-[70ch] font-sans text-[18px] leading-[1.65] text-ink">
            {strings.shop.intro}
          </p>
        </Row>
      </Section>

      <Section className="!py-6">
        <Row>
          <div className="flex flex-wrap gap-2" data-testid="category-filter">
            <button
              type="button"
              onClick={() => setCategory('')}
              aria-pressed={active === ''}
              data-testid="category-all"
              className={chipClass(active === '')}
            >
              {strings.shop.allCategories}
            </button>
            {topLevel.map((category) => (
              <button
                key={category.slug}
                type="button"
                onClick={() => setCategory(category.slug)}
                aria-pressed={active === category.slug || activeTopSlug === category.slug}
                data-testid="category-option"
                data-slug={category.slug}
                className={chipClass(active === category.slug || activeTopSlug === category.slug)}
              >
                {category.name}
              </button>
            ))}
          </div>

          {subcategories.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="subcategory-filter">
              <button
                type="button"
                onClick={() => setCategory(activeTopSlug)}
                aria-pressed={active === activeTopSlug}
                data-testid="subcategory-all"
                className={chipClass(active === activeTopSlug)}
              >
                {strings.shop.allCategories}
              </button>
              {subcategories.map((category) => (
                <button
                  key={category.slug}
                  type="button"
                  onClick={() => setCategory(category.slug)}
                  aria-pressed={active === category.slug}
                  data-testid="subcategory-option"
                  data-slug={category.slug}
                  className={chipClass(active === category.slug)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          ) : null}
        </Row>
      </Section>

      <Section className="!pt-6">
        <Row>
          {loading && products.length === 0 ? (
            <p className="font-sans text-[18px] text-slate">{strings.loading}</p>
          ) : products.length === 0 ? (
            <p className="font-sans text-[18px] text-slate" data-testid="shop-empty">
              {strings.shop.empty}
            </p>
          ) : (
            <div
              className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-2 lg:grid-cols-3"
              data-testid="product-grid"
            >
              {products.map((product, index) => (
                <BlurbCard
                  key={product.id}
                  filename={product.images[0]?.filename ?? ''}
                  alt={product.images[0]?.alt ?? product.name}
                  title={product.name}
                  description={product.subtitle}
                  to={productPath(locale, product.slug)}
                  eager={index < 3}
                  imageAspect="aspect-[3/4]"
                  footer={
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <span className="font-sans text-[16px] font-semibold text-ink">
                        {formatPrice(product, locale)}
                      </span>
                      <StatusBadge status={product.status} label={statusLabel(product.status)} />
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </Row>
      </Section>
    </>
  )
}
